import type { LanguageModel } from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import { asText } from "@prismicio/helpers";
import { useLocation } from "@remix-run/react";
import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import type { LayoutDocumentDataLanguagesItem } from "~/lib/prismic";

const LanguageSelector: HTMLComponent<{
  languageNames: LayoutDocumentDataLanguagesItem[];
  languages: LanguageModel[];
  locale: string;
}> = ({ languageNames, languages, locale }) => {
  const isHydrated = useHydrated();
  const location = useLocation();
  const [altRoutes, setAltRoutes] = useState<Array<LanguageModel & { altRoute?: string }> | undefined>();
  useEffect(() => {
    if (isHydrated)
      setAltRoutes(() =>
        languages?.map((lang) => {
          const altRoute = (
            document.querySelector(
              `link[rel="alternate"][hreflang="${lang.country}-${lang.defaultLang}"]`
            ) as HTMLLinkElement
          )?.href;

          return { ...lang, altRoute };
        })
      );
  }, [isHydrated, location]);

  const mouseEnter = () => {
    const myElement = document.getElementById("wvheader-language-trigger")!;
    if (myElement) {
      myElement.classList.add("active");
    }
  };
  const mouseLeave = () => {
    const myElement = document.getElementById("wvheader-language-trigger")!;
    if (myElement) {
      myElement.classList.remove("active");
    }
  };
  const [lang, setLang] = useState(locale.split("-")[1]);
  if (lang != null) {
    languages = languages.filter((x) => x.defaultLang !== lang);
  }

  const getLanguageName = (code: string) => {
    const language = languageNames.find(
      (x) =>
        asText(x.locale)?.trim().toLowerCase() === code?.trim().toLowerCase()
    )?.language;
    return language ? asText(language) : "";
  };

  return (
    <div
      className="col-auto py-2 pl-3 pr-2 ml-2 ml-4 text-center d-none d-lg-block"
      onMouseEnter={() => mouseEnter()}
      onMouseLeave={() => mouseLeave()}
    >
      <div className="wvheader__language">
        <div className="mr-3">
          <svg width="16" height="23" xmlns="http://www.w3.org/2000/svg">
            <g fill="#FFF" fillRule="nonzero">
              <path d="M7.986 0C3.583 0 0 3.627 0 8.086c0 1.506.412 2.976 1.192 4.252l6.34 10.343a.664.664 0 00.565.319h.005a.664.664 0 00.566-.328l6.178-10.443a8.153 8.153 0 001.126-4.143C15.972 3.627 12.39 0 7.986 0zm5.718 11.537l-5.617 9.495-5.764-9.404a6.792 6.792 0 01-1-3.542c0-3.716 2.993-6.747 6.663-6.747 3.67 0 6.66 3.031 6.66 6.747a6.796 6.796 0 01-.942 3.45z"></path>
              <path d="M7.986 3.833a4.157 4.157 0 00-4.153 4.153 4.147 4.147 0 004.153 4.153 4.145 4.145 0 004.153-4.153 4.157 4.157 0 00-4.153-4.153zm0 6.93a2.78 2.78 0 01-2.778-2.777 2.787 2.787 0 012.778-2.778 2.784 2.784 0 012.773 2.778 2.775 2.775 0 01-2.773 2.778z"></path>
            </g>
          </svg>
        </div>
        <span className="d-block wvheader__icon-text" data-icon-after="">
          {lang?.toUpperCase()}
        </span>
      </div>
      <div
        className="px-3 wvheader__languagelist js-header-language-list"
        id="wvheader-language-trigger"
      >
        {altRoutes?.map((item) => (
          <Fragment key={`${item.country}-${item.defaultLang}`}>
            <a
              href={item.altRoute ?? `https://${item.domain}`}
              target="_blank"
              className="py-2 row flex-nowrap"
              onClick={() => {
                setLang(lang);
              }}
              rel="noopener noreferrer"
            >
              <div className="text-left col d-flex align-items-center">
                <div
                  className={classNames(
                    "fflag ff-md mr-3",
                    `fflag-${item?.defaultLang.toUpperCase()}`
                  )}
                ></div>
                <span>{getLanguageName(item.defaultLang)}</span>
              </div>
              <div className="col-auto">
                <span data-icon-after=""></span>
              </div>
            </a>
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export default LanguageSelector;
